import classNames from 'classnames'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'

import MainMedia from './MainMedia'
import RightColumnContent from './RightColumnContent'
import type { TechnicalContentProps } from './types'
import {
  normalizeTechnicalContentGallery,
  normalizeTechnicalContentItems,
  normalizeTechnicalContentProduct,
} from './types'

export default function TechnicalContent20({
  enableSession = true,
  theme = 'light',
  items = [],
  imageGallery = [],
  product,
}: TechnicalContentProps) {
  const parsedItems = useMemo(
    () => normalizeTechnicalContentItems(items),
    [items]
  )

  const parsedGallery = useMemo(
    () => normalizeTechnicalContentGallery(imageGallery),
    [imageGallery]
  )

  const parsedProduct = useMemo(
    () => normalizeTechnicalContentProduct(product),
    [product]
  )

  const shouldReduceMotion = useReducedMotion()
  const fadeDuration = shouldReduceMotion ? 0 : 0.2

  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (!parsedItems.length) {
      return
    }

    if (activeIndex >= parsedItems.length) {
      setActiveIndex(0)
    }
  }, [activeIndex, parsedItems.length])

  if (
    !enableSession ||
    parsedItems.length === 0 ||
    parsedGallery.length === 0 ||
    !parsedProduct
  ) {
    return null
  }

  const currentItem = parsedItems[activeIndex] ?? parsedItems[0]
  const isSingleItem = parsedItems.length <= 1

  const goToPreviousItem = () => {
    if (isSingleItem) {
      return
    }

    setActiveIndex((previousIndex) =>
      previousIndex === 0 ? parsedItems.length - 1 : previousIndex - 1
    )
  }

  const goToNextItem = () => {
    if (isSingleItem) {
      return
    }

    setActiveIndex((previousIndex) =>
      previousIndex === parsedItems.length - 1 ? 0 : previousIndex + 1
    )
  }

  const goToFirstItem = () => {
    setActiveIndex(0)
  }

  const goToLastItem = () => {
    setActiveIndex(parsedItems.length - 1)
  }

  return (
    <section
      className={classNames('w-full pb-[45px] md:pb-[80px]', {
        'bg-technicalContent20SurfaceInverted text-technicalContent20TitleInverted':
          theme === 'dark',
        'bg-white text-technicalContent20Title': theme !== 'dark',
      })}
      data-testid="technical-content-2-0"
      aria-label="Conteudo tecnico"
    >
      <div className="mx-auto grid w-full max-w-[1440px] grid-cols-1 gap-[30px] md:gap-0 px-5 py-5 md:grid-cols-[640px_minmax(0,1fr)] md:px-0">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`technical-content-media-${activeIndex}`}
            className="h-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: fadeDuration, ease: 'easeInOut' }}
          >
            <MainMedia media={currentItem.mainMedia} theme={theme} />
          </motion.div>
        </AnimatePresence>

        <RightColumnContent
          item={currentItem}
          imageGallery={parsedGallery}
          product={parsedProduct}
          activeIndex={activeIndex}
          totalItems={parsedItems.length}
          theme={theme}
          onPrevious={goToPreviousItem}
          onNext={goToNextItem}
          onGoToStart={goToFirstItem}
          onGoToEnd={goToLastItem}
        />
      </div>
    </section>
  )
}
